import React, { useState, useEffect } from 'react';
import { X, Copy, RefreshCw, Database, Trash2, ExternalLink } from 'lucide-react';
import { FleetSensor } from '../types';
import { fetchRawReadings, deleteReadings, RawReading } from '../services/adminService';

// The Table Editor addresses tables by an internal numeric id that isn't
// derivable from here, so the link opens the editor and the UUID goes to the
// clipboard for the filter box rather than pretending to pre-fill it.
const SUPABASE_PROJECT_URL = 'https://supabase.com/dashboard/project/afymeineqmlssedmqudk';

interface Props {
  sensor: FleetSensor;
  onClose: () => void;
}

// Shows what a device is actually sending, unflattened. The fleet table maps a
// handful of known keys into columns, so anything new or unexpected a device
// reports is invisible there — which is exactly what you need to see when
// something looks wrong.
const RawReadingsModal: React.FC<Props> = ({ sensor, onClose }) => {
  const [rows, setRows] = useState<RawReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pending, setPending] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState(false);

  const copyId = () => {
    navigator.clipboard.writeText(sensor.sensorId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  // Local midnight, not UTC: the operator picks a calendar day as they live it.
  const range = () => ({
    fromISO: new Date(`${from}T00:00:00`).toISOString(),
    toISO: new Date(`${to}T00:00:00`).toISOString(),
  });

  const preview = async () => {
    setBusy(true); setMsg(null);
    const { fromISO, toISO } = range();
    const r = await deleteReadings(sensor.sensorId, fromISO, toISO, true);
    setBusy(false);
    if ('error' in r) { setMsg(r.error); setPending(null); return; }
    setPending(r.count);
  };

  const confirm = async () => {
    setBusy(true); setMsg(null);
    const { fromISO, toISO } = range();
    const r = await deleteReadings(sensor.sensorId, fromISO, toISO, false);
    setBusy(false);
    setPending(null);
    if ('error' in r) { setMsg(r.error); return; }
    setMsg(`${r.count} lecturas borradas.`);
    load();
  };

  const load = async () => {
    setLoading(true);
    setRows(await fetchRawReadings(sensor.sensorId, 100));
    setLoading(false);
  };

  useEffect(() => { load(); }, [sensor.sensorId]);

  const copyAll = () => {
    navigator.clipboard.writeText(JSON.stringify(rows, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-3xl h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Database className="w-5 h-5 text-cyan-400 shrink-0" />
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-100 truncate">Raw readings</h2>
              <p className="text-xs text-slate-500 font-mono truncate">{sensor.deviceId}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={load} title="Reload" className="p-2 text-slate-400 hover:text-cyan-400">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={copyAll} title="Copy all as JSON" className="p-2 text-slate-400 hover:text-cyan-400">
              <Copy className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-5 py-2 border-b border-slate-700 shrink-0">
          <p className="text-xs text-slate-500">
            {loading ? 'Loading…' : `${rows.length} most recent, newest first`}
            {copied && <span className="text-emerald-400 ml-2">copied</span>}
          </p>
        </div>

        <div className="px-5 py-2 border-b border-slate-700 shrink-0 flex flex-wrap items-center gap-2">
          <a href={`${SUPABASE_PROJECT_URL}/editor?schema=public`} target="_blank" rel="noreferrer"
             className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
            <ExternalLink className="w-3.5 h-3.5" /> Abrir Table Editor
          </a>
          <button onClick={copyId} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
            <Copy className="w-3.5 h-3.5" /> {copiedId ? 'UUID copiado' : 'Copiar UUID del sensor'}
          </button>
          <span className="text-[11px] text-slate-600 font-mono truncate">{sensor.sensorId}</span>
        </div>

        <div className="px-5 py-3 border-b border-slate-700 shrink-0 space-y-2">
          <p className="text-xs text-slate-400 flex items-center gap-1.5">
            <Trash2 className="w-3.5 h-3.5" /> Borrar un rango (datos de prueba, ruido)
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPending(null); }}
                   className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
            <span className="text-xs text-slate-500">hasta (sin incluir)</span>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setPending(null); }}
                   className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200" />
            <button onClick={preview} disabled={!from || !to || busy}
                    className="px-3 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-slate-100">
              Ver cuántas
            </button>
          </div>
          {pending !== null && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-amber-400">Se borrarán {pending} lecturas. No se puede deshacer.</span>
              <button onClick={confirm} disabled={busy || pending === 0}
                      className="px-3 py-1 rounded bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white">
                Borrar
              </button>
              <button onClick={() => setPending(null)} className="text-slate-400 hover:text-slate-200">Cancelar</button>
            </div>
          )}
          {msg && <p className="text-xs text-slate-300">{msg}</p>}
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-2">
          {!loading && rows.length === 0 && (
            <p className="text-sm text-slate-500">This device has never reported.</p>
          )}
          {rows.map((r, i) => (
            <div key={i} className="border border-slate-700 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-slate-900/60 text-xs text-slate-400 font-mono">
                {new Date(r.createdAt).toLocaleString()}
              </div>
              <pre className="px-3 py-2 text-xs text-slate-300 font-mono overflow-x-auto whitespace-pre">
{JSON.stringify(r.payload, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RawReadingsModal;
